import Layout from "../../components/Layout/Layout";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import QuickOnlineAccountServices, { ONLINE_ACCOUNT_APP_NAME } from "../../services/QuickOnlineAccountServices";
import { validateDate } from "./../../utils/convertDate";
import Spinner from "./../../utils/Spinner";
import fire from "./../../utils/Toastr";
import { ArrowNarrowLeftIcon } from "@heroicons/react/solid";
import { Link } from "react-router-dom";
import Modal from "../../components/Modals/preonboard/modal";
import LivenessImagesPanel from "../../components/LivenessImagesPanel";

const TAB_LIST = [
  { key: "overview", label: "Overview" },
  { key: "bvn", label: "BVN Details" },
  { key: "nin", label: "NIN Details" },
  { key: "image", label: "Identity Image" },
];

const spanClass = { 2: "sm:col-span-2", 3: "sm:col-span-3", 6: "sm:col-span-6" };

function Field({ label, value, span = 2 }) {
  return (
    <div className={spanClass[span] || "sm:col-span-2"}>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <p className="mt-1 font-semibold text-gray-900 break-words">{value || "N/A"}</p>
    </div>
  );
}

function SectionCard({ title, children, action }) {
  return (
    <div className="relative overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white">
      <div className="min-w-full py-2 align-middle md:px-6 lg:px-8 bg-white">
        <div className="p-6">
          <div className="flex items-center justify-between border-b pb-2 mb-4">
            <h4 className="text-lg font-bold text-gray-900">{title}</h4>
            {action}
          </div>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function NoDataMessage({ message }) {
  return (
    <div className="p-4">
      <SectionCard title="No Data Available">
        <div className="sm:col-span-6 text-center py-8">
          <p className="text-gray-500 text-sm">{message}</p>
        </div>
      </SectionCard>
    </div>
  );
}

export default function ViewOnlineAccount() {
  const id = new URLSearchParams(window.location.search).get("id");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (tab) => setSearchParams({ id: id || "", tab });
  const [acct, setAcct] = useState({});
  const [loading, setLoading] = useState(true);
  const [pndLoading, setPndLoading] = useState(false);
  const [showPndModal, setShowPndModal] = useState(false);
  const [bvnImg, setBvnImg] = useState(null);

  const getAcct = async () => {
    setLoading(true);
    const resp = await QuickOnlineAccountServices.viewAccount(id);
    if (resp && resp.data) {
      setAcct(resp.data);
      const bvnDetails = resp.data.bvnDetails;
      if (bvnDetails?.imageUrl) {
        setBvnImg(bvnDetails.imageUrl);
      } else if (bvnDetails?.base64Image) {
        try {
          const response = await fetch(bvnDetails.base64Image);
          const imageBlob = await response.blob();
          const reader = new FileReader();
          reader.readAsDataURL(imageBlob);
          reader.onloadend = () => {
            setBvnImg(reader.result);
          };
        } catch (error) {
          console.error("Error loading BVN image:", error);
        }
      }
    }
    setLoading(false);
  };

  const liftPnd = async () => {
    setPndLoading(true);
    try {
      const resp = await QuickOnlineAccountServices.removePnd(acct.accountNumber);
      if (resp && resp.status) {
        fire("success", resp.message || "PND lifted successfully");
        setShowPndModal(false);
        setAcct((prev) => ({ ...prev, accountOnPND: false }));
      } else {
        fire("error", resp?.message || "Failed to lift PND");
      }
    } catch (err) {
      fire("error", "Failed to lift PND");
    } finally {
      setPndLoading(false);
    }
  };

  useEffect(() => {
    getAcct();
  }, []);

  if (loading) {
    return (
      <Layout pageTag={"account"} current={"/online/accounts"}>
        <div className="flex justify-center items-center min-h-screen">
          <Spinner />
        </div>
      </Layout>
    );
  }

  const hasBvn = !!acct.bvnDetails;
  const hasNin = !!acct.ninSummaryData;
  const hasImage = !!(
    acct.imageUrl ||
    acct.bvnDetails?.bvn ||
    acct.bvn
  );

  return (
    <Layout pageTag={"account"} current={"/online/accounts"}>
      <main>
        <div className="max-w-7xl mx-auto">
          <div className="py-4">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to="/online/accounts"
                className="inline-flex items-center text-gray-500 hover:text-gray-700"
              >
                <ArrowNarrowLeftIcon className="h-5 w-5 mr-1" />
                Back
              </Link>
            </div>

            <div className="flex justify-center mb-6">
              <div className="flex flex-col items-center">
                {(acct.imageUrl) && (
                  <img
                    src={(acct.imageUrl)}
                    alt="Customer"
                    className="h-24 w-24 rounded-full object-cover border-4 border-gray-200 shadow mb-3"
                  />
                )}
                <h4 className="text-xl font-bold text-gray-900">
                  {acct.surname || acct.bvnDetails?.lastName || "N/A"} {acct.firstName || acct.bvnDetails?.firstName || "N/A"}
                </h4>
                <span className="text-sm text-gray-500">{acct.accountNumber}</span>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <div className="flex items-center justify-between">
                <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
                  {TAB_LIST.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.key
                          ? "border-red-600 text-red-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      {tab.label}
                      {tab.key === "bvn" && hasBvn && (
                        <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-800 text-xs">✓</span>
                      )}
                      {tab.key === "nin" && hasNin && (
                        <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-800 text-xs">✓</span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            <div className="px-4 sm:px-6 lg:px-8">
              <div className="mt-2 flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8 bg-zinc-200 rounded-lg">
                    {/* OVERVIEW TAB */}
                    {activeTab === "overview" && (
                      <div className="space-y-5 p-4">
                        <SectionCard title="Personal Information">
                          <Field label="Title" value={acct.title || acct.bvnDetails?.title} />
                          <Field label="First Name" value={acct.firstName || acct.bvnDetails?.firstName} />
                          <Field label="Surname" value={acct.surname || acct.bvnDetails?.lastName} />
                          <Field label="Date of Birth" value={acct.dateofBirth ? validateDate(acct.dateofBirth) : acct.bvnDetails?.dateOfBirth} />
                          <Field label="Gender" value={acct.gender || acct.bvnDetails?.gender} />
                          <Field label="Marital Status" value={acct.maritalStatus || acct.bvnDetails?.maritalStatus} />
                          <Field label="NIN" value={acct.nin || acct.bvnDetails?.nin} />
                          <Field label="Phone Number" value={acct.phoneNumber || acct.bvnDetails?.phoneNumber} />
                          <Field label="Email Address" value={acct.emailAddress || acct.bvnDetails?.email} />
                          <Field label="Residential Address" value={acct.residentialAddress || acct.bvnDetails?.residentialAddress} span={3} />
                          <Field label="State of Residence" value={acct.stateOfResidence || acct.bvnDetails?.stateOfResidence} />
                        </SectionCard>

                        <SectionCard
                          title="Account Information"
                          action={
                            acct.accountOnPND === true && (
                              <button
                                type="button"
                                onClick={() => setShowPndModal(true)}
                                className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 whitespace-nowrap"
                              >
                                {pndLoading ? <Spinner /> : "Lift PND"}
                              </button>
                            )
                          }
                        >
                          <Field label="Account Number" value={acct.accountNumber} />
                          <Field label="Customer Number" value={acct.customerNumber} />
                          <Field label="Account Opening Medium" value={acct.accountOpeningMedium} />
                          <Field label="Account Opening Stage" value={acct.accountOpeningStage} />
                          <Field label="Account Opening Class" value={acct.accountOpeningClass} />
                          <Field label="Account On PND?" value={acct.accountOnPND ? "Yes" : "No"} />
                          <Field label="Is Image Similar?" value={acct.isImageSimilar ? "Yes" : "No"} />
                          <Field label="Date Created" value={validateDate(acct.createdOn)} />
                        </SectionCard>
                      </div>
                    )}

                    {/* BVN DETAILS TAB */}
                    {activeTab === "bvn" && (
                      hasBvn ? (
                        <div className="p-4">
                          <SectionCard title="BVN Verification Details">
                            {bvnImg && (
                              <div className="sm:col-span-6 flex justify-center py-2 mb-2">
                                <img
                                  src={bvnImg}
                                  alt="BVN"
                                  className="inline-block h-28 w-28 rounded-full border-8 border-rose-600"
                                />
                              </div>
                            )}
                            <Field label="BVN" value={acct.bvnDetails.bvn} />
                            <Field label="First Name" value={acct.bvnDetails.firstName} />
                            <Field label="Middle Name" value={acct.bvnDetails.middleName} />
                            <Field label="Last Name" value={acct.bvnDetails.lastName} />
                            <Field label="Date of Birth" value={acct.bvnDetails.dateOfBirth} />
                            <Field label="Gender" value={acct.bvnDetails.gender} />
                            <Field label="Phone Number" value={acct.bvnDetails.phoneNumber} />
                            <Field label="Email" value={acct.bvnDetails.email} />
                            <Field label="Title" value={acct.bvnDetails.title} />
                            <Field label="Nationality" value={acct.bvnDetails.nationality} />
                            <Field label="Marital Status" value={acct.bvnDetails.maritalStatus} />
                            <Field label="NIN" value={acct.bvnDetails.nin} />
                            <Field label="Name on Card" value={acct.bvnDetails.nameOnCard} />
                            <Field label="Enrollment Bank" value={acct.bvnDetails.enrollmentBank} />
                            <Field label="Enrollment Branch" value={acct.bvnDetails.enrollmentBranch} />
                            <Field label="Registration Date" value={acct.bvnDetails.registrationDate} />
                            <Field label="Level of Account" value={acct.bvnDetails.levelOfAccount} />
                            <Field label="Watch Listed" value={acct.bvnDetails.watchListed} />
                            <Field label="State of Origin" value={acct.bvnDetails.stateOfOrigin} />
                            <Field label="LGA of Origin" value={acct.bvnDetails.lgaOfOrigin} />
                            <Field label="State of Residence" value={acct.bvnDetails.stateOfResidence} />
                            <Field label="LGA of Residence" value={acct.bvnDetails.lgaOfResidence} />
                            <Field label="Residential Address" value={acct.bvnDetails.residentialAddress} span={3} />
                            <Field label="Alternative Phone Number 1" value={acct.bvnDetails.phoneNumber1} />
                            <Field label="Alternative Phone Number 2" value={acct.bvnDetails.phoneNumber2} />
                          </SectionCard>
                        </div>
                      ) : (
                        <NoDataMessage message="No BVN details available for this account." />
                      )
                    )}

                    {/* NIN DETAILS TAB */}
                    {activeTab === "nin" && (
                      hasNin ? (
                        <div className="p-4">
                          <SectionCard title="NIN Verification Details">
                            <Field label="First Name" value={acct.ninSummaryData.firstName} />
                            <Field label="Middle Name" value={acct.ninSummaryData.middleName} />
                            <Field label="Last Name" value={acct.ninSummaryData.lastName} />
                            <Field label="Gender" value={acct.ninSummaryData.gender} />
                            <Field label="Date of Birth" value={acct.ninSummaryData.dateOfBirth} />
                            <Field label="Phone" value={acct.ninSummaryData.phone1} />
                            <Field label="Match" value={acct.ninSummaryData.match} />
                            <Field label="Message" value={acct.ninSummaryData.message} />
                            <Field label="Biometric Type" value={acct.ninSummaryData.biometricType} />
                            <Field label="Biometric Sub Type" value={acct.ninSummaryData.biometricSubType} />
                            <Field label="Error" value={acct.ninSummaryData.error} />
                            {acct.ninSummaryData.image && (
                              <div className="sm:col-span-6 flex justify-center py-4">
                                <img
                                  src={acct.ninSummaryData.image}
                                  alt="NIN"
                                  className="h-48 w-48 object-cover rounded-lg border"
                                />
                              </div>
                            )}
                            {acct.ninSummaryData.errors && (
                              <Field label="Error Message" value={acct.ninSummaryData.errors?.message} span={3} />
                            )}
                          </SectionCard>
                        </div>
                      ) : (
                        <NoDataMessage message="No NIN verification data available for this account." />
                      )
                    )}

                    {/* IDENTITY IMAGE TAB */}
                    {activeTab === "image" && (
                      hasImage ? (
                        <div className="p-4">
                          <SectionCard title="Identity Image">
                            {acct.imageUrl && (
                              <div className="sm:col-span-6 flex justify-center py-6">
                                <img
                                  src={acct.imageUrl}
                                  alt="Identity"
                                  className="h-80 w-80 object-cover rounded-lg border-2 border-gray-200 shadow-lg"
                                />
                              </div>
                            )}
                            <div className="sm:col-span-6">
                  <LivenessImagesPanel
                    searchTerm={acct?.bvnDetails?.bvn || acct?.bvn}
                    appName={ONLINE_ACCOUNT_APP_NAME}
                  />
                            </div>
                          </SectionCard>
                        </div>
                      ) : (
                        <NoDataMessage message="No identity image available for this account." />
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Modal
        open={showPndModal}
        setOpen={setShowPndModal}
        textField="to lift the PND on this account?"
        status={"Pnd"}
        loading={pndLoading}
        action={liftPnd}
      />
    </Layout>
  );
}
